import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { RESOURCE_LIMIT_EXCEEDED_MESSAGE } from 'constants/errorMessages';
import { REGION_SUBSCRIPTION_PLANS } from 'constants/routes';
import { useContext } from 'react';
import { useAuthStore } from 'stores/authStore';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import { openUpgradeTierModal } from 'utils/handleTierExceededError';
import PRODUCTS_KEYS from '../products.keys';
import PRODUCTS_MUTATIONS from '../products.mutation';
import PRODUCTS_QUERIES from '../products.queries';

export const useFetchChildProducts = ({ page, pageSize, search, ordering, filters }) =>
  useQuery({
    queryKey: PRODUCTS_KEYS.CHILD_PRODUCTS_PAGINATED({ page, pageSize, search, ordering, filters }),
    queryFn: () =>
      PRODUCTS_QUERIES.FETCH_CHILD_PRODUCTS({
        page,
        pageSize,
        search,
        ordering,
        filters,
      }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchChildProductsInfinite = ({ pageSize, search, ordering, filters }) =>
  useInfiniteQuery({
    queryKey: PRODUCTS_KEYS.CHILD_PRODUCTS_INFINITE({ pageSize, filters, search, ordering }),
    queryFn: ({ pageParam = 1 }) =>
      PRODUCTS_QUERIES.FETCH_CHILD_PRODUCTS({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchAllChildProducts = ({ page, pageSize, search, ordering, filters }) =>
  useQuery({
    queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS_PAGINATED({
      page,
      pageSize,
      search,
      ordering,
      filters,
    }),
    queryFn: () =>
      PRODUCTS_QUERIES.FETCH_ALL_CHILD_PRODUCTS({
        page,
        pageSize,
        search,
        ordering,
        filters,
      }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchAllChildProductsInfinite = ({ pageSize, search, ordering, filters }) =>
  useInfiniteQuery({
    queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS_INFINITE({
      pageSize,
      filters,
      search,
      ordering,
    }),
    queryFn: ({ pageParam = 1 }) =>
      PRODUCTS_QUERIES.FETCH_ALL_CHILD_PRODUCTS({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateChildProduct = () => {
  const queryClient = useQueryClient();
  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const user = useAuthStore((state) => state.user);
  const { closePopup } = useContext(PopupContext);

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.CREATE_CHILD_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.CHILD_PRODUCTS() });
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS() });
      closePopup?.();
    },
    onError: (error) => {
      if (error.message === RESOURCE_LIMIT_EXCEEDED_MESSAGE) {
        closePopup?.();

        const limit = user?.admin_details?.brand.regions.find(
          (region) => region.uid === selectedRegion,
        ).subscription.max_products;
        const upgradeUrl = REGION_SUBSCRIPTION_PLANS.replace(':id', selectedRegion);

        openUpgradeTierModal({
          resourceType: 'products',
          currentLimit: limit,
          upgradeUrl,
        });
      }
    },
  });
};

export const useUpdateChildProduct = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.UPDATE_CHILD_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.CHILD_PRODUCTS() });
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS() });
      closePopup?.();
    },
  });
};

export const usePatchChildProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.PATCH_CHILD_PRODUCT,
    onMutate: async (updatedProduct) => {
      await queryClient.cancelQueries({ queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS() });

      const previousData = queryClient.getQueriesData({
        queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS(),
      });

      queryClient.setQueriesData({ queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS() }, (oldData) => {
        if (!oldData) return oldData;

        if (oldData.results) {
          return {
            ...oldData,
            results: oldData.results.map((product) =>
              product.uid === updatedProduct.uid ? { ...product, ...updatedProduct } : product,
            ),
          };
        }

        if (Array.isArray(oldData)) {
          return oldData.map((product) =>
            product.uid === updatedProduct.uid ? { ...product, ...updatedProduct } : product,
          );
        }

        return oldData;
      });
      return { previousData };
    },

    onError: (err, updatedProduct, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS() });
    },
  });
};

export const useDeleteChildProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_CHILD_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS() });
      // Also invalidate favorite products as they contain child products
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.FAVORITE_PRODUCTS() });
    },
  });
};
